/**
 * Withholding tax calculation for receipt flows.
 * - Modal types in invoiceBaseModalTypes: (invoice total × percent / 100)
 * - Other receipt types: gross-up on net payment lines
 */

/** Default: חשבונית מס קבלה (CreateReceipt modal type 5) */
const DEFAULT_INVOICE_BASE_MODAL_TYPES = [5];

/**
 * @param {object} params
 * @param {number} params.modalType Invoice modal type for this screen
 * @param {number} params.base Invoice / sale total (used for invoice-base formula)
 * @param {number} params.totalReceiptAmount Net receipt (payment lines, excluding withholding)
 * @param {number|string|null|undefined} params.activePercent e.g. 10 for 10%
 * @param {number[]} [params.invoiceBaseModalTypes] Modal types that use base × percent / 100
 * @returns {number} Rounded withholding amount (2 decimals)
 */
export const calculateWithholdingAmount = ({
    modalType,
    base,
    totalReceiptAmount,
    activePercent,
    invoiceBaseModalTypes = DEFAULT_INVOICE_BASE_MODAL_TYPES,
}) => {
    if (activePercent == null || activePercent === '') {
        return 0;
    }
    const percent = Number(activePercent);
    if (!Number.isFinite(percent) || percent <= 0) {
        return 0;
    }

    const useInvoiceTotalBase = invoiceBaseModalTypes
        .map(Number)
        .includes(Number(modalType));

    if (useInvoiceTotalBase) {
        const invoiceBase = Number(base) || 0;
        if (invoiceBase <= 0) {
            return 0;
        }
        return Number(((invoiceBase * percent) / 100).toFixed(2));
    }

    const receiptTotal = Number(totalReceiptAmount) || 0;
    if (receiptTotal <= 0 || percent >= 100) {
        return 0;
    }

    const divisor = (100 - percent) / 100;
    if (!Number.isFinite(divisor) || divisor <= 0) {
        return 0;
    }

    const grossAmount = receiptTotal / divisor;
    if (!Number.isFinite(grossAmount)) {
        return 0;
    }

    const calculatedWithholdingAmount = grossAmount - receiptTotal;
    if (!Number.isFinite(calculatedWithholdingAmount) || calculatedWithholdingAmount < 0) {
        return 0;
    }

    return Number(calculatedWithholdingAmount.toFixed(2));
};
